import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, BookOpen, GraduationCap, Layers } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { WorksheetCard, SUBJECT_LABELS, formatGrade } from "./WorksheetCard";
import type { Worksheet } from "@/services/worksheet.service";
import type { CurriculumOutcome } from "@/services/curriculum.service";

// ============================================================================
// Types
// ============================================================================

interface WorksheetCurriculumViewProps {
  worksheets: Worksheet[];
  outcomes: CurriculumOutcome[];
}

interface OutcomeWithWorksheets {
  outcome: CurriculumOutcome;
  worksheets: Worksheet[];
}

interface SubjectGroup {
  subject: string;
  subjectLabel: string;
  outcomes: OutcomeWithWorksheets[];
  totalWorksheets: number;
}

interface GradeGroup {
  grade: string;
  gradeLabel: string;
  subjects: SubjectGroup[];
  totalWorksheets: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build hierarchical structure: Grade -> Subject -> Outcome -> Worksheets
 */
function buildHierarchy(
  worksheets: Worksheet[],
  outcomes: CurriculumOutcome[]
): GradeGroup[] {
  // Create a map of outcome_id -> outcome
  const outcomeMap = new Map<string, CurriculumOutcome>();
  for (const outcome of outcomes) {
    outcomeMap.set(outcome.id, outcome);
  }

  // Create a map of outcome_id -> worksheets
  const worksheetsByOutcome = new Map<string, Worksheet[]>();
  for (const worksheet of worksheets) {
    for (const outcomeId of worksheet.outcome_ids) {
      const existing = worksheetsByOutcome.get(outcomeId) || [];
      // Avoid duplicates (a worksheet could be linked to multiple outcomes)
      if (!existing.some((w) => w.id === worksheet.id)) {
        existing.push(worksheet);
      }
      worksheetsByOutcome.set(outcomeId, existing);
    }
  }

  // Group outcomes by grade and subject
  const gradeMap = new Map<string, Map<string, OutcomeWithWorksheets[]>>();

  for (const outcome of outcomes) {
    const outcomeWorksheets = worksheetsByOutcome.get(outcome.id) || [];

    // Only include outcomes that have worksheets
    if (outcomeWorksheets.length === 0) continue;

    if (!gradeMap.has(outcome.grade)) {
      gradeMap.set(outcome.grade, new Map());
    }

    const subjectMap = gradeMap.get(outcome.grade)!;
    if (!subjectMap.has(outcome.subject)) {
      subjectMap.set(outcome.subject, []);
    }

    subjectMap.get(outcome.subject)!.push({
      outcome,
      worksheets: outcomeWorksheets,
    });
  }

  // Convert to array structure and sort
  const result: GradeGroup[] = [];

  // Sort grades: Kindergarten first, then numerically
  const sortedGrades = Array.from(gradeMap.keys()).sort((a, b) => {
    if (a === "Kindergarten") return -1;
    if (b === "Kindergarten") return 1;
    // Extract numeric part for sorting
    const aNum = parseInt(a.replace(/\D/g, ""), 10) || 0;
    const bNum = parseInt(b.replace(/\D/g, ""), 10) || 0;
    return aNum - bNum;
  });

  for (const grade of sortedGrades) {
    const subjectMap = gradeMap.get(grade)!;
    const subjects: SubjectGroup[] = [];

    // Sort subjects alphabetically
    const sortedSubjects = Array.from(subjectMap.keys()).sort();

    for (const subject of sortedSubjects) {
      const outcomesWithWorksheets = subjectMap.get(subject)!;
      const totalWorksheets = outcomesWithWorksheets.reduce(
        (sum, o) => sum + o.worksheets.length,
        0
      );

      subjects.push({
        subject,
        subjectLabel: SUBJECT_LABELS[subject.toLowerCase().replace(/ /g, "_")] || subject,
        outcomes: outcomesWithWorksheets,
        totalWorksheets,
      });
    }

    const totalWorksheets = subjects.reduce((sum, s) => sum + s.totalWorksheets, 0);

    result.push({
      grade,
      gradeLabel: grade === "Kindergarten" ? grade : formatGrade(grade.replace("Grade ", "")),
      subjects,
      totalWorksheets,
    });
  }

  return result;
}

// ============================================================================
// Components
// ============================================================================

interface OutcomeSectionProps {
  outcomeWithWorksheets: OutcomeWithWorksheets;
}

function OutcomeSection({ outcomeWithWorksheets }: OutcomeSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { outcome, worksheets } = outcomeWithWorksheets;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="ml-8">
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 text-left hover:bg-muted/50 rounded-md transition-colors">
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <BookOpen className="h-4 w-4 shrink-0 text-primary/70" />
        <span className="flex-1 text-sm line-clamp-2">{outcome.outcomeText}</span>
        <Badge variant="secondary" className="shrink-0">
          {worksheets.length} {worksheets.length === 1 ? "worksheet" : "worksheets"}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="ml-6 mt-2 mb-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {worksheets.map((worksheet) => (
            <WorksheetCard key={worksheet.id} worksheet={worksheet} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface SubjectSectionProps {
  subjectGroup: SubjectGroup;
}

function SubjectSection({ subjectGroup }: SubjectSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="ml-4">
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 text-left hover:bg-muted/50 rounded-md transition-colors">
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <Layers className="h-4 w-4 shrink-0 text-blue-500" />
        <span className="flex-1 font-medium">{subjectGroup.subjectLabel}</span>
        <Badge variant="outline" className="shrink-0">
          {subjectGroup.totalWorksheets} {subjectGroup.totalWorksheets === 1 ? "worksheet" : "worksheets"}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 space-y-1">
        {subjectGroup.outcomes.map((outcomeWithWorksheets) => (
          <OutcomeSection
            key={outcomeWithWorksheets.outcome.id}
            outcomeWithWorksheets={outcomeWithWorksheets}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface GradeSectionProps {
  gradeGroup: GradeGroup;
  defaultOpen?: boolean;
}

function GradeSection({ gradeGroup, defaultOpen = false }: GradeSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg bg-card">
      <CollapsibleTrigger className="flex items-center gap-3 w-full p-4 text-left hover:bg-muted/30 rounded-lg transition-colors">
        {isOpen ? (
          <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
        )}
        <GraduationCap className="h-5 w-5 shrink-0 text-primary" />
        <span className="flex-1 text-lg font-semibold">{gradeGroup.gradeLabel}</span>
        <Badge className="shrink-0">
          {gradeGroup.totalWorksheets} {gradeGroup.totalWorksheets === 1 ? "worksheet" : "worksheets"}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-4 space-y-2">
        {gradeGroup.subjects.map((subjectGroup) => (
          <SubjectSection key={subjectGroup.subject} subjectGroup={subjectGroup} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function WorksheetCurriculumView({
  worksheets,
  outcomes,
}: WorksheetCurriculumViewProps) {
  // Build the hierarchical structure
  const hierarchy = useMemo(
    () => buildHierarchy(worksheets, outcomes),
    [worksheets, outcomes]
  );

  if (hierarchy.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No worksheets are linked to curriculum outcomes yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hierarchy.map((gradeGroup, index) => (
        <GradeSection
          key={gradeGroup.grade}
          gradeGroup={gradeGroup}
          defaultOpen={index === 0}
        />
      ))}
    </div>
  );
}
